import React, { useState, useEffect } from 'react';
import { configAPI } from '../services/api';
import { Upload, Download, Settings, AlertCircle, Database, Trash2 } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const Config = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(null);

  useEffect(() => {
    fetchConfig();
  }, []);

  // Auto-refresh for multi-user visibility (15-20 second interval)
  useEffect(() => {
    // Start auto-refresh when component mounts
    const interval = setInterval(() => {
      fetchConfig();
    }, 18000); // 18 seconds interval

    setAutoRefreshInterval(interval);

    // Cleanup on unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await configAPI.getConfig();
      setConfig(response.data);
    } catch (err) {
      if (err.response?.status !== 404) {
        setError(err.response?.data?.message || 'Не удалось получить конфиг');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      setError('Выберите JSON-файл');
      return;
    }

    try {
      setUploadLoading(true);
      setError('');
      setSuccess('');

      const text = await file.text();
      const configData = JSON.parse(text);

      if (!Array.isArray(configData)) {
        setError('Конфиг должен быть массивом объектов хостов');
        return;
      }

      // Validate config structure
      for (const item of configData) {
        if (!item.host || typeof item.host !== 'string') {
          setError('Каждый элемент конфига должен содержать корректное поле host');
          return;
        }
      }

      await configAPI.updateConfig(configData);
      setSuccess('Конфиг успешно обновлён');
      await fetchConfig();
    } catch (err) {
      if (err instanceof SyntaxError) {
        setError('Невалидный формат JSON');
      } else {
        setError(err.response?.data?.message || 'Не удалось загрузить конфиг');
      }
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setDownloadLoading(true);
      const response = await configAPI.downloadConfig();
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], {
        type: 'application/json',
      });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'config.json';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось скачать конфиг');
    } finally {
      setDownloadLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleClearConfigDatabase = async () => {
    try {
      setClearing(true);
      setError('');
      setSuccess('');
      
      const response = await configAPI.clearConfigDatabase();
      setSuccess(`База конфигов успешно очищена. Удалено: ${response.data.deleted_configs} конфигов, ${response.data.deleted_hosts} хостов и ${response.data.deleted_diffs} диффов.`);
      setShowClearDialog(false);
      await fetchConfig();
    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось очистить базу конфигов');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div>Загрузка конфига...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px' }}>
      <div className="page-header">
        <div className="page-header-icon">
          <Settings size={24} />
        </div>
        <div className="page-header-text">
          <h1>Управление конфигом</h1>
          <p>Загрузка и управление файлом конфигурации</p>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Database Management */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <h3 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: 'var(--text-primary)',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <Database size={20} />
          Управление базой данных
        </h3>
        
        <p style={{
          color: 'var(--text-secondary)',
          marginBottom: '16px',
          fontSize: '14px',
        }}>
          Удалить из базы все данные конфига, хосты и диффы. Это действие необратимо.
        </p>
        
        <button
          onClick={() => setShowClearDialog(true)}
          className="btn btn-danger"
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          <Trash2 size={16} />
          Очистить базу конфигов
        </button>
      </div>

      <div className="grid grid-2">
        {/* Upload Section */}
        <div className="card">
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Upload size={20} />
            Загрузить конфиг
          </h3>

          <div style={{ marginBottom: '16px' }}>
            <label className="form-label" htmlFor="config-file">
              Выберите JSON-файл
            </label>
            <input
              type="file"
              id="config-file"
              accept=".json"
              onChange={handleFileUpload}
              disabled={uploadLoading}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>

          <div style={{
            backgroundColor: 'var(--bg-tertiary)',
            padding: '12px',
            borderRadius: '6px',
            fontSize: '14px',
            color: 'var(--text-secondary)',
          }}>
            <strong>Ожидаемый формат:</strong>
            <pre style={{
              marginTop: '8px',
              fontSize: '12px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              padding: '8px',
              borderRadius: '4px',
              overflow: 'auto',
            }}>
{`[
  {
    "host": "example.com",
    "tech": ["Nginx", "PHP"],
    "modify": {
      "Last-Modified": null,
      "ETag": null
    },
    "locations": [
      {
        "url": "https://example.com",
        "status_code": 200,
        "size": 1024
      }
    ],
    "relations": {
      "domains": ["example.com"],
      "links": [
        {
          "url": "https://example.com/page1",
          "host": "example.com",
          "status_code": 200,
          "size": 512
        }
      ]
    }
  }
]`}
            </pre>
          </div>

          {uploadLoading && (
            <div style={{
              marginTop: '16px',
              textAlign: 'center',
              color: '#3b82f6',
            }}>
              Загрузка и обработка конфига...
            </div>
          )}
        </div>

        {/* Download Section */}
        <div className="card">
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Download size={20} />
            Скачать конфиг
          </h3>

          <p style={{
            color: 'var(--text-secondary)',
            marginBottom: '16px',
            fontSize: '14px',
          }}>
            Скачать текущий файл конфигурации в формате JSON.
          </p>

          <button
            onClick={handleDownload}
            disabled={downloadLoading || !config}
            className="btn btn-primary"
            style={{ width: '100%' }}
          >
            {downloadLoading ? 'Скачивание...' : 'Скачать config.json'}
          </button>

          {!config && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#92400e',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <AlertCircle size={16} />
              Нет файла конфига для скачивания
            </div>
          )}
        </div>
      </div>

      {/* Current Config Info */}
      {config && (
        <div className="card">
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}>
            <Settings size={20} />
            Информация о текущем конфиге
          </h3>

          <div className="grid grid-3">
            <div>
              <div style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
                marginBottom: '4px',
              }}>
                Создан
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: 'var(--text-primary)',
              }}>
                {formatDate(config.created_at)}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
                marginBottom: '4px',
              }}>
                Создал
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: 'var(--text-primary)',
              }}>
                {config.created_by}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
                marginBottom: '4px',
              }}>
                Hash
              </div>
              <div style={{
                fontSize: '12px',
                fontFamily: 'monospace',
                color: 'var(--text-primary)',
                backgroundColor: 'var(--bg-tertiary)',
                padding: '4px 8px',
                borderRadius: '4px',
                wordBreak: 'break-all',
              }}>
                {config.hash}
              </div>
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <div style={{
              fontSize: '14px',
              color: 'var(--text-secondary)',
              marginBottom: '8px',
            }}>
              Предпросмотр конфига (первые 3 элемента):
            </div>
            <div style={{
              backgroundColor: 'var(--bg-tertiary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              padding: '12px',
              maxHeight: '200px',
              overflowY: 'auto',
            }}>
              <pre style={{
                margin: 0,
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                color: 'var(--text-primary)',
              }}>
                {JSON.stringify(config.data.slice(0, 3), null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showClearDialog}
        onClose={() => setShowClearDialog(false)}
        onConfirm={handleClearConfigDatabase}
        title="Очистить базу конфигов"
        message="Вы уверены, что хотите очистить всю базу конфигов? Это удалит все конфиги, хосты и диффы. Это действие необратимо."
        confirmText={clearing ? 'Очистка...' : 'Очистить базу'}
        cancelText="Отмена"
      />
    </div>
  );
};

export default Config;
